import React, { useState, useRef, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Cpu,
  Factory,
  Globe,
  Hash,
  Loader2,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Plus,
  Search,
  ShieldCheck,
  Tag,
  Trash2,
  Wrench,
  X,
  Building2,
  Save,
  Clock,
  CheckCircle2,
  Filter,
} from "lucide-react";
import { plantsAPI, machinesAPI } from "../api";
import clsx from "clsx";
import toast from "react-hot-toast";
import { formatIstDate } from "../utils/date";
import HasPermission from "../components/HasPermission";
import { PERMISSIONS } from "../constants/roles";
import AddMachine from "./AddMachine";

const statusConfig = {
  operational: {
    label: "Operational",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200/80",
    dot: "bg-emerald-500",
    iconBg: "bg-emerald-50 text-emerald-600 border border-emerald-100",
  },
  maintenance: {
    label: "In Maintenance",
    badge: "bg-amber-50 text-amber-700 border-amber-200/80",
    dot: "bg-amber-500",
    iconBg: "bg-amber-50 text-amber-600 border border-amber-100",
  },
  error: {
    label: "Error",
    badge: "bg-rose-50 text-rose-700 border-rose-200/80",
    dot: "bg-rose-500",
    iconBg: "bg-rose-50 text-rose-600 border border-rose-100",
  },
  offline: {
    label: "Offline",
    badge: "bg-slate-100 text-slate-700 border-slate-200",
    dot: "bg-slate-400",
    iconBg: "bg-slate-100 text-slate-600 border border-slate-200",
  },
};

/**
 * StatCard component styled identically to MachineDetail.jsx
 */
const StatCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  color = "blue",
}) => {
  const colorThemes = {
    blue: {
      bg: "from-blue-50/90 to-white border-blue-100/80 hover:border-blue-200",
      icon: "bg-blue-100/80 text-blue-600",
      badge: "bg-blue-100 text-blue-700",
    },
    emerald: {
      bg: "from-emerald-50/90 to-white border-emerald-100/80 hover:border-emerald-200",
      icon: "bg-emerald-100/80 text-emerald-600",
      badge: "bg-emerald-100 text-emerald-700",
    },
    amber: {
      bg: "from-amber-50/90 to-white border-amber-100/80 hover:border-amber-200",
      icon: "bg-amber-100/80 text-amber-600",
      badge: "bg-amber-100 text-amber-700",
    },
    rose: {
      bg: "from-rose-50/90 to-white border-rose-100/80 hover:border-rose-200",
      icon: "bg-rose-100/80 text-rose-600",
      badge: "bg-rose-100 text-rose-700",
    },
    indigo: {
      bg: "from-indigo-50/90 to-white border-indigo-100/80 hover:border-indigo-200",
      icon: "bg-indigo-100/80 text-indigo-600",
      badge: "bg-indigo-100 text-indigo-700",
    },
  };

  const theme = colorThemes[color] || colorThemes.blue;

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={clsx(
        "p-4 sm:p-5 bg-gradient-to-br border rounded-2xl shadow-xs hover:shadow-md transition-all duration-200 flex flex-col justify-between relative overflow-hidden group",
        theme.bg
      )}
    >
      <div>
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className={clsx("p-2.5 rounded-xl flex-shrink-0", theme.icon)}>
            <Icon className="w-5 h-5" />
          </div>
          {subtitle && (
            <span
              className={clsx(
                "inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full truncate",
                theme.badge
              )}
            >
              {subtitle}
            </span>
          )}
        </div>
        <p className="text-xs sm:text-sm font-medium text-slate-500 mb-1">
          {title}
        </p>
        <p className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
          {value}
        </p>
      </div>
    </motion.div>
  );
};

/**
 * Plant Edit Modal (matches AddMachine.jsx styling)
 */
const EditPlantModal = ({ isOpen, plant, onClose }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: "",
    location: "",
    address: "",
    timezone: "UTC",
    contactEmail: "",
    contactPhone: "",
  });

  useEffect(() => {
    if (plant && isOpen) {
      setFormData({
        name: plant.name || "",
        location: plant.location || "",
        address: plant.address || "",
        timezone: plant.timezone || "UTC",
        contactEmail: plant.contact_email || plant.contactEmail || "",
        contactPhone: plant.contact_phone || plant.contactPhone || "",
      });
    }
  }, [plant, isOpen]);

  const updateMutation = useMutation({
    mutationFn: (data) => plantsAPI.update(plant.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plant", String(plant.id)] });
      queryClient.invalidateQueries({ queryKey: ["plants"] });
      toast.success("Plant facility updated successfully!");
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Failed to update plant");
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Plant name is required");
      return;
    }
    updateMutation.mutate(formData);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-sm overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget && !updateMutation.isPending) onClose();
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 10 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="relative w-full max-w-xl bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden flex flex-col my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 sm:p-6 border-b border-slate-100 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 border border-blue-100/80 flex items-center justify-center flex-shrink-0">
              <Factory className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight">
                Edit Plant Facility
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Update facility information, regional location and contacts
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col">
          <div className="p-5 sm:p-6 space-y-4 sm:space-y-5 overflow-y-auto max-h-[calc(85vh-130px)]">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Plant Name <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full pl-10 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Location / Unit
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="w-full pl-10 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Timezone
                </label>
                <div className="relative">
                  <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    value={formData.timezone}
                    onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                    className="w-full pl-10 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Contact Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                    className="w-full pl-10 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Contact Phone
                </label>
                <div className="relative">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  <input
                    type="text"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                    className="w-full pl-10 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                Full Facility Address
              </label>
              <textarea
                rows={3}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm resize-none"
              />
            </div>
          </div>

          <div className="p-4 sm:p-5 bg-slate-50/80 border-t border-slate-100 flex items-center justify-end gap-2.5">
            <button
              type="button"
              onClick={onClose}
              disabled={updateMutation.isPending}
              className="btn btn-secondary text-xs sm:text-sm py-2 px-4 hover:border-slate-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="btn btn-primary text-xs sm:text-sm py-2 px-4 shadow-md shadow-blue-500/20 whitespace-nowrap"
            >
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-1.5" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

/**
 * Plant Equipment Card (Matches Machines.jsx MachineCard)
 */
const PlantMachineCard = ({ machine, plantName, index, onEdit, onDelete }) => {
  const status = statusConfig[machine.status] || statusConfig.offline;
  const activeAlerts = Number(machine.active_alerts) || 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(index * 0.03, 0.3) }}
      className="h-full"
    >
      <Link
        to={`/machines/${machine.id}`}
        className="group bg-white rounded-2xl border border-slate-200/80 p-5 hover:border-blue-300 hover:shadow-md transition-all duration-200 flex flex-col justify-between h-full relative block cursor-pointer"
      >
        <div>
          {/* Header */}
          <div className="flex items-start justify-between gap-2.5 mb-4">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div
                className={clsx(
                  "w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-105",
                  status.iconBg
                )}
              >
                <Cpu className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-slate-900 group-hover:text-blue-600 transition-colors text-sm sm:text-base leading-snug break-words">
                  {machine.name}
                </h3>
                <p className="text-xs text-slate-500 font-medium mt-0.5 break-words">
                  {machine.model || machine.serial_number || "Machine Unit"}
                </p>
                <p className="text-xs text-blue-600 font-medium mt-1 break-words">
                  {plantName}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 flex-shrink-0 -mr-1 -mt-0.5">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEdit(machine);
                }}
                className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Edit Machine"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete(machine);
                }}
                className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                title="Delete Machine"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Machine Info Grid */}
          <div className="grid grid-cols-2 gap-2.5 mb-2">
            <div className="bg-slate-50/80 border border-slate-100 rounded-xl p-2.5">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-0.5 flex items-center gap-1">
                <Factory className="w-3 h-3 text-slate-400 flex-shrink-0" />
                <span>Manufacturer</span>
              </p>
              <p className="text-xs font-semibold text-slate-700 break-words">
                {machine.manufacturer || "OEM Generic"}
              </p>
            </div>
            <div className="bg-slate-50/80 border border-slate-100 rounded-xl p-2.5">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-0.5 flex items-center gap-1">
                <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />
                <span>Location</span>
              </p>
              <p className="text-xs font-semibold text-slate-700 break-words">
                {machine.location_in_plant || "Plant Floor"}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="pt-3.5 border-t border-slate-100 flex items-center justify-between gap-2 text-xs mt-auto">
          {activeAlerts > 0 ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-rose-700 bg-rose-50 border border-rose-200/90 py-1 px-2.5 rounded-xl">
              <AlertTriangle className="w-3.5 h-3.5 text-rose-600 flex-shrink-0" />
              <span>{activeAlerts} active alert{activeAlerts > 1 ? "s" : ""}</span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span>No active alerts</span>
            </span>
          )}

          <span
            className={clsx(
              "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border flex-shrink-0 ml-auto",
              status.badge
            )}
          >
            {status.label}
          </span>
        </div>
      </Link>
    </motion.div>
  );
};

const PlantDetail = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState("machines");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 8;

  const [isAddMachineOpen, setIsAddMachineOpen] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [deletingMachine, setDeletingMachine] = useState(null);
  const [isEditPlantOpen, setIsEditPlantOpen] = useState(false);

  const filterRef = useRef(null);

  const { data: response, isLoading, isError } = useQuery({
    queryKey: ["plant", id],
    queryFn: () => plantsAPI.getById(id).then((res) => res.data),
  });

  const plant = response?.data;
  const machines = plant?.machines || [];

  // Delete machine mutation
  const deleteMutation = useMutation({
    mutationFn: (machineId) => machinesAPI.delete(machineId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["plant", id] });
      queryClient.invalidateQueries({ queryKey: ["machines"] });
      queryClient.invalidateQueries({ queryKey: ["plants"] });
      toast.success("Machine deleted successfully");
      setDeletingMachine(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Failed to delete machine");
    },
  });

  // Calculate statistics
  const totalMachines = machines.length;
  const operationalCount = machines.filter(
    (m) => (m.status || "").toLowerCase() === "operational"
  ).length;
  const totalAlerts = machines.reduce(
    (acc, m) => acc + (Number(m.active_alerts) || 0),
    0
  );
  const maintenanceCount = machines.filter(
    (m) => (m.status || "").toLowerCase() === "maintenance"
  ).length;

  // Filtered machines
  const filteredMachines = machines.filter((machine) => {
    const matchesSearch =
      !search ||
      machine.name?.toLowerCase().includes(search.toLowerCase()) ||
      machine.model?.toLowerCase().includes(search.toLowerCase()) ||
      machine.serial_number?.toLowerCase().includes(search.toLowerCase()) ||
      machine.manufacturer?.toLowerCase().includes(search.toLowerCase());

    const matchesStatus =
      !statusFilter ||
      (machine.status || "").toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.max(1, Math.ceil(filteredMachines.length / PAGE_SIZE));
  const displayedMachines = filteredMachines.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [filterRef]);

  const tabs = [
    { id: "machines", label: "Connected Equipment", icon: Cpu, count: totalMachines },
    { id: "overview", label: "Facility Overview", icon: Building2 },
    { id: "contact", label: "Operations & Contact", icon: Phone },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between animate-pulse">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-200" />
            <div className="space-y-2">
              <div className="h-6 w-48 bg-slate-200 rounded" />
              <div className="h-4 w-32 bg-slate-100 rounded" />
            </div>
          </div>
          <div className="h-9 w-32 bg-slate-200 rounded-xl" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-slate-200/80 p-5 space-y-3 animate-pulse"
            >
              <div className="w-10 h-10 rounded-xl bg-slate-200" />
              <div className="h-4 w-24 bg-slate-100 rounded" />
              <div className="h-7 w-20 bg-slate-200 rounded" />
            </div>
          ))}
        </div>
        <div className="h-10 w-72 rounded-full bg-slate-200/60 animate-pulse" />
        <div className="h-64 bg-white border border-slate-200/80 rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (isError || !plant) {
    return (
      <div className="p-12 text-center bg-white border border-rose-200 rounded-2xl space-y-3">
        <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
          <AlertTriangle className="w-6 h-6" />
        </div>
        <h3 className="text-base font-bold text-slate-900">Plant Facility Not Found</h3>
        <p className="text-xs text-slate-500 max-w-sm mx-auto">
          The requested plant facility could not be retrieved. It may have been removed or deactivated.
        </p>
        <Link
          to="/plants"
          className="btn btn-secondary text-xs sm:text-sm py-2 px-4 inline-flex items-center gap-1.5 mt-2"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Plants</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link
            to="/plants"
            className="p-2.5 bg-white text-slate-600 hover:text-slate-900 border border-slate-200/80 hover:border-slate-300 rounded-xl shadow-xs transition-all flex-shrink-0"
            title="Back to Plants Fleet"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>

          <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center flex-shrink-0 shadow-xs">
            <Factory className="w-5 h-5" />
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight break-words">
                {plant.name}
              </h1>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/80">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5" />
                Active Facility
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs sm:text-sm text-slate-500 mt-0.5">
              <span>{plant.location || "Main Facility"}</span>
              <span>•</span>
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3.5 h-3.5 text-slate-400" />
                {plant.timezone || "UTC"}
              </span>
              <span>•</span>
              <span>Registered {formatIstDate(plant.created_at)}</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2.5 sm:gap-3 flex-wrap sm:flex-nowrap self-start sm:self-auto">
          <HasPermission allowedRoles={PERMISSIONS.MANAGE_MACHINES}>
            <button
              type="button"
              onClick={() => setIsEditPlantOpen(true)}
              className="btn btn-secondary text-xs sm:text-sm py-2 px-3.5 shadow-xs"
            >
              <Pencil className="w-4 h-4 text-slate-500" />
              <span>Edit Facility</span>
            </button>
          </HasPermission>

          <HasPermission allowedRoles={PERMISSIONS.MANAGE_MACHINES}>
            <button
              type="button"
              onClick={() => {
                setEditingMachine(null);
                setIsAddMachineOpen(true);
              }}
              className="btn btn-primary text-xs sm:text-sm py-2 px-3.5 shadow-md shadow-blue-500/20 whitespace-nowrap"
            >
              <Plus className="w-4 h-4" />
              <span>Add Equipment</span>
            </button>
          </HasPermission>
        </div>
      </div>

      {/* 4 Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
        <StatCard
          title="Connected Fleet"
          value={totalMachines}
          subtitle={totalMachines === 1 ? "1 Machine" : `${totalMachines} Machines`}
          icon={Cpu}
          color="blue"
        />
        <StatCard
          title="Operational Status"
          value={`${totalMachines > 0 ? Math.round((operationalCount / totalMachines) * 100) : 0}%`}
          subtitle={`${operationalCount} of ${totalMachines} Active`}
          icon={ShieldCheck}
          color="emerald"
        />
        <StatCard
          title="Active Alerts"
          value={totalAlerts}
          subtitle={totalAlerts === 0 ? "Normal Fleet" : "Attention Required"}
          icon={AlertTriangle}
          color={totalAlerts === 0 ? "emerald" : "rose"}
        />
        <StatCard
          title="In Maintenance"
          value={maintenanceCount}
          subtitle={maintenanceCount === 0 ? "None in Service" : "Active Service"}
          icon={Wrench}
          color="amber"
        />
      </div>

      {/* Tabs Pill Navigation */}
      <div>
        <div className="inline-flex items-center gap-1 p-1 bg-white rounded-full border border-slate-200/80 shadow-xs max-w-full overflow-x-auto no-scrollbar">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  "px-4 sm:px-5 py-2 text-xs sm:text-sm rounded-full transition-all duration-200 flex items-center gap-2 whitespace-nowrap shrink-0",
                  isActive
                    ? "bg-blue-600 text-white shadow-sm shadow-blue-500/25 font-bold"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-200/60 font-medium"
                )}
              >
                <Icon
                  className={clsx(
                    "w-4 h-4",
                    isActive ? "text-white" : "text-slate-500"
                  )}
                />
                <span>{tab.label}</span>
                {tab.count !== undefined && (
                  <span
                    className={clsx(
                      "px-1.5 py-0.2 rounded-full text-[10px] font-bold",
                      isActive
                        ? "bg-white/20 text-white"
                        : "bg-slate-100 text-slate-600"
                    )}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab 1: Connected Equipment Fleet */}
      {activeTab === "machines" && (
        <div className="space-y-4 sm:space-y-5">
          {/* Controls Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="relative flex-1 sm:max-w-md">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search equipment by name, model, serial..."
                className="w-full pl-9 pr-9 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 shadow-sm transition-all"
              />
              {search && (
                <button
                  onClick={() => {
                    setSearch("");
                    setCurrentPage(1);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <div ref={filterRef} className="relative w-full sm:w-auto">
                <button
                  onClick={() => setIsFilterOpen((prev) => !prev)}
                  className="btn btn-secondary w-full sm:w-auto justify-between h-9 text-xs sm:text-sm px-3.5 bg-white shadow-sm hover:border-slate-400"
                >
                  <div className="flex items-center gap-2">
                    <Filter className="w-3.5 h-3.5 text-slate-500" />
                    {statusFilter && (
                      <div
                        className={clsx(
                          "w-2 h-2 rounded-full",
                          statusConfig[statusFilter]?.dot || "bg-slate-400"
                        )}
                      />
                    )}
                    <span className="font-semibold text-slate-700">
                      {statusConfig[statusFilter]?.label || "All Status"}
                    </span>
                  </div>
                  <ChevronDown
                    className={clsx(
                      "w-3.5 h-3.5 text-slate-500 transition-transform duration-200",
                      isFilterOpen && "rotate-180"
                    )}
                  />
                </button>
                <AnimatePresence>
                  {isFilterOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.98 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-1.5 w-full sm:w-52 origin-top-right rounded-xl bg-white shadow-lg border border-slate-200/90 py-1.5 z-20"
                    >
                      <button
                        onClick={() => {
                          setStatusFilter("");
                          setCurrentPage(1);
                          setIsFilterOpen(false);
                        }}
                        className={clsx(
                          "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium",
                          statusFilter === ""
                            ? "bg-blue-50 text-blue-700 font-semibold"
                            : "text-slate-700 hover:bg-slate-50"
                        )}
                      >
                        <span>All Status</span>
                        {statusFilter === "" && (
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                        )}
                      </button>
                      {Object.entries(statusConfig).map(([key, config]) => (
                        <button
                          key={key}
                          onClick={() => {
                            setStatusFilter(key);
                            setCurrentPage(1);
                            setIsFilterOpen(false);
                          }}
                          className={clsx(
                            "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium",
                            statusFilter === key
                              ? "bg-blue-50 text-blue-700 font-semibold"
                              : "text-slate-700 hover:bg-slate-50"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <span className={clsx("w-2 h-2 rounded-full", config.dot)} />
                            <span>{config.label}</span>
                          </div>
                          {statusFilter === key && (
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                          )}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Cards Grid */}
          {displayedMachines.length > 0 ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-5">
                {displayedMachines.map((machine, index) => (
                  <PlantMachineCard
                    key={machine.id}
                    machine={machine}
                    plantName={plant.name}
                    index={index}
                    onEdit={(m) => setEditingMachine(m)}
                    onDelete={(m) => setDeletingMachine(m)}
                  />
                ))}
              </div>

              {/* Pagination if items exceed PAGE_SIZE */}
              {filteredMachines.length > PAGE_SIZE && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-slate-200/80">
                  <p className="text-xs text-slate-500 font-medium">
                    Showing{" "}
                    <span className="font-semibold text-slate-700">
                      {(currentPage - 1) * PAGE_SIZE + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-semibold text-slate-700">
                      {Math.min(currentPage * PAGE_SIZE, filteredMachines.length)}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-slate-700">
                      {filteredMachines.length}
                    </span>{" "}
                    equipment
                  </p>

                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      disabled={currentPage <= 1}
                      onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                      className={clsx(
                        "h-8 px-2.5 rounded-xl border text-xs font-medium flex items-center gap-1 transition-colors",
                        currentPage <= 1
                          ? "border-slate-200 text-slate-300 cursor-not-allowed bg-slate-50"
                          : "border-slate-200 text-slate-700 bg-white hover:bg-slate-50 active:bg-slate-100 shadow-xs"
                      )}
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Prev</span>
                    </button>

                    <button
                      type="button"
                      disabled={currentPage >= totalPages}
                      onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                      className={clsx(
                        "h-8 px-2.5 rounded-xl border text-xs font-medium flex items-center gap-1 transition-colors",
                        currentPage >= totalPages
                          ? "border-slate-200 text-slate-300 cursor-not-allowed bg-slate-50"
                          : "border-slate-200 text-slate-700 bg-white hover:bg-slate-50 active:bg-slate-100 shadow-xs"
                      )}
                    >
                      <span className="hidden sm:inline">Next</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-10 sm:p-14 bg-white border border-dashed border-slate-300 rounded-2xl text-center flex flex-col items-center justify-center">
              <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-3">
                <Cpu className="w-7 h-7" />
              </div>
              <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1">
                No Equipment Assigned
              </h3>
              <p className="text-xs sm:text-sm text-slate-500 max-w-sm mb-5">
                {search || statusFilter
                  ? "No machines matched your current search and status filter. Try resetting filters."
                  : "No industrial machines are assigned to this plant yet. Add equipment to initiate telemetry and health monitoring."}
              </p>
              {search || statusFilter ? (
                <button
                  onClick={() => {
                    setSearch("");
                    setStatusFilter("");
                    setCurrentPage(1);
                  }}
                  className="btn btn-secondary text-xs sm:text-sm py-2 px-4"
                >
                  Reset Filters
                </button>
              ) : (
                <HasPermission allowedRoles={PERMISSIONS.MANAGE_MACHINES}>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingMachine(null);
                      setIsAddMachineOpen(true);
                    }}
                    className="btn btn-primary inline-flex text-xs sm:text-sm py-2 px-4 shadow-md shadow-blue-500/20"
                  >
                    <Plus className="w-4 h-4" />
                    Add Equipment
                  </button>
                </HasPermission>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Facility Overview */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 bg-white border border-slate-200/80 rounded-2xl p-5 sm:p-6 shadow-xs space-y-6">
            <div>
              <h2 className="text-base font-bold text-slate-900">
                Facility Specifications & Location
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Physical property, operational timezone and installation baseline
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-slate-400" />
                  <span>Facility Location</span>
                </span>
                <p className="text-sm font-semibold text-slate-800">
                  {plant.location || "Location not assigned"}
                </p>
              </div>

              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1 flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5 text-slate-400" />
                  <span>Operating Timezone</span>
                </span>
                <p className="text-sm font-semibold text-slate-800">
                  {plant.timezone || "UTC"}
                </p>
              </div>

              <div className="sm:col-span-2 p-3.5 rounded-xl bg-slate-50 border border-slate-100">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1 flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 text-slate-400" />
                  <span>Full Physical Address</span>
                </span>
                <p className="text-sm font-semibold text-slate-800 break-words leading-relaxed">
                  {plant.address || "Address details not recorded"}
                </p>
              </div>
            </div>

            {/* Custom Metadata if present */}
            {plant.metadata && Object.keys(plant.metadata).length > 0 && (
              <div className="pt-4 border-t border-slate-100 space-y-3">
                <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                  Extended Facility Metadata
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  {Object.entries(plant.metadata).map(([key, val]) => (
                    <div
                      key={key}
                      className="p-2.5 rounded-lg bg-slate-50 border border-slate-100"
                    >
                      <span className="text-slate-400 uppercase tracking-wide text-[10px] font-semibold">
                        {key.replaceAll("_", " ")}
                      </span>
                      <p className="text-slate-800 font-semibold mt-0.5">
                        {String(val)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Plant Snapshot Card */}
          <div className="bg-slate-950 text-white rounded-2xl p-6 shadow-sm border border-slate-800 flex flex-col justify-between">
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                <Factory className="w-4 h-4 text-blue-400" />
                <span>Facility Snapshot</span>
              </div>
              <div>
                <p className="text-4xl font-bold text-white tracking-tight">
                  {totalMachines}
                </p>
                <p className="text-sm text-slate-400 mt-1">
                  Active equipment units installed in this plant
                </p>
              </div>

              <div className="space-y-2 pt-4 border-t border-slate-800 text-xs">
                <div className="flex justify-between items-center text-slate-300">
                  <span>Operational Fleet:</span>
                  <span className="font-semibold text-emerald-400">
                    {operationalCount} units
                  </span>
                </div>
                <div className="flex justify-between items-center text-slate-300">
                  <span>In Maintenance:</span>
                  <span className="font-semibold text-amber-400">
                    {maintenanceCount} units
                  </span>
                </div>
                <div className="flex justify-between items-center text-slate-300">
                  <span>Active Fault Alerts:</span>
                  <span className="font-semibold text-rose-400">
                    {totalAlerts} faults
                  </span>
                </div>
              </div>
            </div>

            <div className="pt-6 mt-6 border-t border-slate-800 text-xs text-slate-500">
              Facility created on {formatIstDate(plant.created_at)}
            </div>
          </div>
        </div>
      )}

      {/* Tab 3: Operations & Contact */}
      {activeTab === "contact" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-white border border-slate-200/80 rounded-2xl p-5 sm:p-6 shadow-xs space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                <Mail className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm sm:text-base font-bold text-slate-900">
                  Contact Dispatch & Operations
                </h3>
                <p className="text-xs text-slate-500">
                  Primary plant manager & operations communications
                </p>
              </div>
            </div>

            <div className="space-y-3 pt-2">
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 block mb-1">
                  Direct Email
                </span>
                <p className="text-sm font-semibold text-slate-800 break-all">
                  {plant.contact_email || "No email assigned"}
                </p>
              </div>

              <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
                <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 block mb-1">
                  Direct Phone / Emergency Desk
                </span>
                <p className="text-sm font-semibold text-slate-800">
                  {plant.contact_phone || "No phone assigned"}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200/80 rounded-2xl p-5 sm:p-6 shadow-xs space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm sm:text-base font-bold text-slate-900">
                  Maintenance Dispatch Protocol
                </h3>
                <p className="text-xs text-slate-500">
                  Automated notifications and field technician routing
                </p>
              </div>
            </div>

            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
              When equipment sensors at {plant.name} record critical telemetry anomalies, work orders are automatically routed to the technicians on duty in accordance with standard operating procedures.
            </p>

            <div className="pt-2">
              <Link to="/assistant">
                <button
                  type="button"
                  className="btn btn-secondary text-xs sm:text-sm py-2 px-3.5 shadow-xs w-full sm:w-auto"
                >
                  <Activity className="w-4 h-4 text-blue-600" />
                  <span>Query AI Assistant for Plant Diagnostics</span>
                </button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {/* Edit Plant Modal */}
      <EditPlantModal
        isOpen={isEditPlantOpen}
        plant={plant}
        onClose={() => setIsEditPlantOpen(false)}
      />

      {/* Add / Edit Machine Modal */}
      <AddMachine
        isOpen={isAddMachineOpen || Boolean(editingMachine)}
        machineToEdit={editingMachine}
        defaultPlantId={id}
        onClose={() => {
          setIsAddMachineOpen(false);
          setEditingMachine(null);
          queryClient.invalidateQueries({ queryKey: ["plant", id] });
        }}
      />

      {/* Delete Machine Confirmation Modal */}
      <AnimatePresence>
        {deletingMachine && (
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-sm overflow-y-auto"
            onClick={() => {
              if (!deleteMutation.isPending) setDeletingMachine(null);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="relative w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-2xl p-6 space-y-4 my-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-600 border border-rose-100/80 flex items-center justify-center shrink-0">
                  <Trash2 className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight">
                    Delete Equipment
                  </h2>
                </div>
              </div>

              <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">
                Are you sure you want to delete{" "}
                <span className="font-semibold text-slate-800">
                  {deletingMachine.name}
                </span>
                ? All sensor logs, work orders, and health history will be permanently deleted.
              </p>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  disabled={deleteMutation.isPending}
                  onClick={() => setDeletingMachine(null)}
                  className="btn btn-secondary text-xs sm:text-sm py-2 px-4 hover:border-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate(deletingMachine.id)}
                  className="btn bg-rose-600 hover:bg-rose-700 text-white text-xs sm:text-sm py-2 px-4 shadow-md shadow-rose-500/20 inline-flex items-center gap-2 font-medium"
                >
                  {deleteMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      <span>Delete Equipment</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PlantDetail;
